import React, { useState } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import {
  ChartBarIcon,
  UserGroupIcon,
  AcademicCapIcon
} from '@heroicons/react/24/outline';

const SurveyResults = () => {
  const [activeTab, setActiveTab] = useState('students');

  const studentData = [
    {
      text: "My teachers use examples of students' different races and cultures in their lessons to make learning more meaningful for me.",
      percentage: 94
    },
    {
      text: "I see a variety of races, ethnicities, cultures, and backgrounds positively represented in the curriculum.",
      percentage: 95
    },
    {
      text: "At this school, we have productive conversations about race and racism where I feel my voice is heard.",
      percentage: 91
    },
    {
      text: "I feel safe…in my classes at this school.",
      percentage: 92
    },
    {
      text: "My teachers are open to students' ideas, suggestions, and comments.",
      percentage: 93
    }
  ];

  return (
    <>
      <Navbar />
      <main id="main-content" className="bg-white">

      {/* Hero Section */}
      <section className="section-padding band-light text-brand-text-primary pt-24 md:pt-32 relative overflow-hidden">
        <div className="content-container relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="relative mb-8">
              <span className="inline-block bg-white shadow-sm text-brand-text-primary px-6 py-3 text-sm font-medium tracking-wider border border-brand-primary/20">
                Community Feedback
              </span>
            </div>
            <h1 className="text-display font-display mb-8 relative">
              <span className="relative">
                Survey Results
              </span>
            </h1>
            <p className="text-xl leading-relaxed max-w-3xl mx-auto">
              Hear what our students and families have to say about their experience at PS/MS 108
            </p>
          </div>
        </div>
      </section>

      {/* Survey Results Section */}
      <section className="section-padding bg-white">
        <div className="content-container">
          <div className="max-w-5xl mx-auto">
            {/* Tab Navigation */}
            <div className="flex flex-col sm:flex-row gap-4 mb-12 border-b border-gray-200" role="tablist" aria-label="Survey results by audience">
              <button
                onClick={() => setActiveTab('students')}
                role="tab"
                aria-selected={activeTab === 'students'}
                id="tab-students"
                aria-controls="tabpanel-students"
                className={`flex items-center justify-center gap-3 px-6 py-4 text-lg font-medium transition-all duration-300 border-b-4 ${
                  activeTab === 'students'
                    ? 'border-brand-primary text-brand-primary bg-brand-primary/5'
                    : 'border-transparent text-gray-600 hover:text-brand-primary hover:bg-gray-50'
                }`}
              >
                <AcademicCapIcon className="w-6 h-6" />
                <span>What are students saying?</span>
              </button>
              <button
                onClick={() => setActiveTab('families')}
                role="tab"
                aria-selected={activeTab === 'families'}
                id="tab-families"
                aria-controls="tabpanel-families"
                className={`flex items-center justify-center gap-3 px-6 py-4 text-lg font-medium transition-all duration-300 border-b-4 ${
                  activeTab === 'families'
                    ? 'border-brand-secondary text-brand-secondary bg-brand-secondary/5'
                    : 'border-transparent text-gray-600 hover:text-brand-secondary hover:bg-gray-50'
                }`}
              >
                <UserGroupIcon className="w-6 h-6" />
                <span>What are families saying?</span>
              </button>
            </div>

            {/* Students Tab Content */}
            {activeTab === 'students' && (
              <div className="space-y-8 animate-fadeIn" role="tabpanel" id="tabpanel-students" aria-labelledby="tab-students">
                <div className="text-center mb-12">
                  <div className="w-20 h-20 bg-brand-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <ChartBarIcon className="w-10 h-10 text-brand-primary" />
                  </div>
                  <h2 className="text-3xl md:text-4xl font-display text-heading mb-4">
                    Student Voice Matters
                  </h2>
                  <p className="text-lg text-body max-w-2xl mx-auto">
                    Our students share their perspectives on inclusion, safety, and learning at PS/MS 108
                  </p>
                </div>

                {studentData.map((item, index) => (
                  <div
                    key={index}
                    className="bg-gray-50 p-6 md:p-8 rounded-xl border border-gray-200 hover:border-brand-primary/30 transition-all duration-300 hover:shadow-lg"
                  >
                    <p className="text-base md:text-lg text-body mb-6 leading-relaxed">
                      "{item.text}"
                    </p>

                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-600">Agreement Rate</span>
                        <span className="text-3xl font-bold text-brand-primary">{item.percentage}%</span>
                      </div>

                      <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
                        <div
                          className="h-full bg-brand-primary rounded-full transition-all duration-1000 ease-out flex items-center justify-end pr-2"
                          style={{ width: `${item.percentage}%` }}
                        >
                          <span className="text-xs font-bold text-white drop-shadow">
                            {item.percentage}%
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                <div className="mt-12 bg-brand-primary/5 border-l-4 border-brand-primary p-6 md:p-8 rounded-r-xl">
                  <p className="text-body text-center italic">
                    "These results reflect our ongoing commitment to creating an inclusive, safe, and engaging learning environment for all students."
                  </p>
                </div>
              </div>
            )}

            {/* Families Tab Content */}
            {activeTab === 'families' && (
              <div className="space-y-8 animate-fadeIn" role="tabpanel" id="tabpanel-families" aria-labelledby="tab-families">
                <div className="text-center mb-12">
                  <div className="w-20 h-20 bg-brand-secondary/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <UserGroupIcon className="w-10 h-10 text-brand-secondary" />
                  </div>
                  <h2 className="text-3xl md:text-4xl font-display text-heading mb-4">
                    Family Feedback
                  </h2>
                  <p className="text-lg text-body max-w-2xl mx-auto">
                    Coming soon - We're gathering feedback from our families
                  </p>
                </div>

                <div className="bg-gray-50 p-12 rounded-xl text-center">
                  <p className="text-gray-500 text-lg">
                    Family survey results will be available here soon.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="section-padding band-warm text-brand-text-primary">
        <div className="content-container">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-display font-display mb-6">
              Your Voice Matters
            </h2>
            <p className="text-xl leading-relaxed mb-8">
              We value the feedback from our students and families as we continually work to improve our school community.
            </p>
            <a
              href="/contact"
              className="inline-flex items-center justify-center bg-brand-primary text-white font-medium px-8 py-4 rounded-lg hover:bg-brand-navy transition-all duration-300"
            >
              Contact Us
            </a>
          </div>
        </div>
      </section>

      </main>
      <Footer />
    </>
  );
};

export default SurveyResults;
